import React, { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Download, FileText, Eye } from 'lucide-react';
import AssessmentAnalysis from '../../components/AssessmentAnalysis';
import ReportTemplate from '../../components/ReportTemplate';

export default function PatientAnalysis() {
    const { user } = useAuth();
    const { assessments } = useData();
    const { id } = useParams(); 
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState('interactive'); // 'interactive' or 'report'

    // If ID is in URL (Admin view), find it. Otherwise (Patient view), grab their latest.
    const myAssessments = user?.patientId ? assessments.filter(a => a.patientId === user.patientId) : assessments;
    const assessmentData = id
        ? assessments.find(a => a.id.toString() === id)
        : (myAssessments.length > 0 ? myAssessments[myAssessments.length - 1] : null);

    const isTherapistView = !!id;

    const handleBack = () => {
        if (isTherapistView) navigate('/app/assessments');
        else navigate('/patient');
    };

    const handlePrint = () => {
        const originalMode = viewMode;
        setViewMode('report');
        setTimeout(() => {
            window.print();
            setViewMode(originalMode);
        }, 500);
    };

    if (!assessmentData) {
        return (
            <div className="max-w-4xl mx-auto pt-12 text-center">
                <h1 className="text-2xl font-bold text-slate-900 font-outfit">Analysis Not Found</h1>
                <Button className="mt-4" onClick={handleBack}>Go Back</Button>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-20">
            {/* Header - Hidden on Print */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 print:hidden">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" className="h-10 w-10 bg-white shadow-soft border border-slate-100 rounded-xl" onClick={handleBack}>
                        <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-black text-slate-900 font-outfit">Evaluation Analysis</h1>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                            {assessmentData.type} • {assessmentData.patientName}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="bg-slate-100 p-1 rounded-xl flex mr-2">
                        <Button 
                            variant={viewMode === 'interactive' ? 'secondary' : 'ghost'} 
                            size="sm" 
                            className="h-8 text-[10px] font-bold uppercase tracking-wider rounded-lg"
                            onClick={() => setViewMode('interactive')}
                        >
                            <Eye className="mr-1.5 h-3 w-3" /> Interactive
                        </Button>
                        <Button 
                            variant={viewMode === 'report' ? 'secondary' : 'ghost'} 
                            size="sm" 
                            className="h-8 text-[10px] font-bold uppercase tracking-wider rounded-lg"
                            onClick={() => setViewMode('report')}
                        >
                            <FileText className="mr-1.5 h-3 w-3" /> Report Preview
                        </Button>
                    </div>
                    <Button variant="primary" className="shadow-premium rounded-xl px-6" onClick={handlePrint}>
                        <Download className="mr-2 h-4 w-4" /> Export PDF
                    </Button>
                </div>
            </div>

            {/* Content Area */}
            <div className="transition-all duration-500">
                {viewMode === 'interactive' ? (
                    <div className="bg-white rounded-[2rem] p-8 shadow-soft border border-slate-50 print:hidden">
                        <AssessmentAnalysis assessmentData={assessmentData} isTherapistView={isTherapistView} />
                    </div>
                ) : (
                    <div className="bg-slate-50 md:p-12 border-2 border-dashed border-slate-200 rounded-[2rem] p-4 print:p-0 print:border-0 print:bg-white print:block">
                        <ReportTemplate assessmentData={assessmentData} />
                    </div>
                )}
            </div>
            
            {/* Fallback Print View for Interactive Mode */}
            {viewMode === 'interactive' && (
                <div className="hidden print:block fixed inset-0 z-[9999] bg-white">
                    <ReportTemplate assessmentData={assessmentData} />
                </div>
            )}
        </div>
    );
}
